const DEFAULT_MAX_DIMENSION = 2048;
const DEFAULT_MAX_BYTES = 10 * 1024 * 1024;
const JPEG_MIME = 'image/jpeg';

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image file'));
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Could not encode image'));
      },
      type,
      quality
    );
  });
}

/**
 * Downscale and re-encode images before upload (target ≤ maxBytes).
 */
export async function compressImageForUpload(
  file,
  { maxDimension = DEFAULT_MAX_DIMENSION, maxBytes = DEFAULT_MAX_BYTES } = {}
) {
  if (!file || !file.type.startsWith('image/')) {
    throw new Error('Please choose an image file');
  }

  const img = await loadImageFromFile(file);
  const scale = Math.min(1, maxDimension / Math.max(img.width, img.height));
  const width = Math.max(1, Math.round(img.width * scale));
  const height = Math.max(1, Math.round(img.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);

  let quality = 0.92;
  let blob = await canvasToBlob(canvas, JPEG_MIME, quality);

  while (blob.size > maxBytes && quality > 0.45) {
    quality -= 0.08;
    blob = await canvasToBlob(canvas, JPEG_MIME, quality);
  }

  if (blob.size > maxBytes) {
    throw new Error('Image is still too large after compression');
  }

  const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
  return new File([blob], `${baseName}.jpg`, { type: JPEG_MIME, lastModified: Date.now() });
}
